/**
 * Trail hints — pure matching between a saved Bucket List place and the live
 * community trails (discovery stops) it belongs to. No writes, no joining.
 */
export interface TrailStopRef {
  stopId: string;
  stopName: string;
  placeName: string | null;
  trailId: string;
  trailTitle: string;
  trailImage: string | null;
  endDate: string;
}

export interface TrailHint extends TrailStopRef {
  joined: boolean;
}

export const normName = (s?: string | null) =>
  (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();

/** "Visit Valgerand Nordic Grill (Saadiyat)" → candidate place names. */
export function placeNamesFromTitle(title: string): string[] {
  const t = title.trim().replace(/^visit\s+/i, "");
  const noArea = t.replace(/\s*\([^)]*\)\s*$/, "");
  return Array.from(new Set([normName(t), normName(noArea)].filter(Boolean)));
}

export const isTrailLive = (endDate: string, today = new Date()) => {
  const end = new Date(`${endDate.slice(0, 10)}T23:59:59`);
  return !isNaN(end.getTime()) && end >= today;
};

/**
 * Pick ONE trail for a place: live trails only; a joined trail wins,
 * otherwise the trail ending soonest. Places on no trail return null.
 */
export function pickTrailHint(
  title: string,
  stops: TrailStopRef[],
  joinedTrailIds: Set<string>,
  today = new Date(),
): TrailHint | null {
  const names = placeNamesFromTitle(title);
  const matches = stops.filter(
    (s) =>
      isTrailLive(s.endDate, today) &&
      (names.includes(normName(s.stopName)) || (s.placeName && names.includes(normName(s.placeName)))),
  );
  if (matches.length === 0) return null;
  const sorted = [...matches].sort((a, b) => {
    const ja = joinedTrailIds.has(a.trailId) ? 0 : 1;
    const jb = joinedTrailIds.has(b.trailId) ? 0 : 1;
    return ja - jb || a.endDate.localeCompare(b.endDate);
  });
  const best = sorted[0];
  return { ...best, joined: joinedTrailIds.has(best.trailId) };
}
